/**
 * The FieldInput component
 */
import React, { Component } from 'react';
import {FormGroup, Input, CustomInput, Label, Alert, InputGroupAddon, InputGroupText, InputGroup} from 'reactstrap';
import PropTypes from "prop-types";

/**
 * Component to override the rendering of a redux-form field. It can render any type of field through the type property
 */
export default class FieldInput extends Component {
  static propTypes = {
    /** The name passed in from redux-form */
    id: PropTypes.string,
    name: PropTypes.string,
    /** The field placeholder */
    placeholder: PropTypes.string,
    /** The field type */
    type: PropTypes.string,
    hint: PropTypes.string,
    autocomplete: PropTypes.string,
    /** The input data passed in from redux-form */
    input: PropTypes.object,
    /** The meta data passed in from redux-form */
    meta: PropTypes.object,
    /** The label passed in from redux-form */
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object
    ]),
    /** The size passed in from redux-form */
    size: PropTypes.string,
    /** The noCustom passed in from redux-form */
    noCustom: PropTypes.bool,
    required: PropTypes.bool,
    min: PropTypes.number,
    maxLength: PropTypes.number,
    max: PropTypes.number,
    style: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object
    ]),
  };

  renderDefault = () => {
    const { id, placeholder, type, input, label, maxLength, size, disabled, className, addonType, addonText, min, max, style, autocomplete, required} = this.props;
    return (
      <div>
        { label &&
          <Label size={size} for={id}>{label}{required && <span>*</span>}</Label>
        }
        { addonType ? 
          <InputGroup>
            <Input className={className} type={type} maxLength={maxLength} required={required} autoComplete={autocomplete} id={id} placeholder={placeholder} value={input.value} onChange={input.onChange}  onFocus={input.onFocus}  onBlur={input.onBlur} bsSize={size} min={ min || min === 0 ?  min : undefined} max={ max || max === 0 ?  max : undefined} disabled={disabled} style={style}>
              {this.props.children}
            </Input>
            <InputGroupAddon addonType={addonType}>
              <InputGroupText>{addonText}</InputGroupText>
            </InputGroupAddon>
          </InputGroup>
          :
          <Input className={className} autoComplete={autocomplete} maxLength={maxLength} required={required} type={type} id={id} placeholder={placeholder} value={input.value} onChange={input.onChange}  onFocus={input.onFocus}  onBlur={input.onBlur} bsSize={size} min={ min || min === 0 ?  min : undefined} max={ max || max === 0 ?  max : undefined} disabled={disabled} style={style}>
            {this.props.children}
          </Input>
        }
        
      </div>
    )
  }

  renderCheckbox = () => {
    const { id, placeholder, type, input, label, size, noCustom, disabled, style, autocomplete, required} = this.props;
    return (
      <div>
        { !noCustom &&
          <CustomInput
            type={type}
            disabled={disabled}
            id={id}
            placeholder={placeholder}
            autoComplete={autocomplete}
            checked={input.value}
            onChange={input.onChange} onClick={input.onClick} onFocus={input.onFocus}  onBlur={input.onBlur}
            bsSize={size}
            required={required}
            label={ typeof label === 'string' ? `${label}${required ? <span>*</span> : ''}` : label}
            style={style}/>
        }
        { noCustom &&
          <Input
            type={type}
            id={id}
            placeholder={placeholder}
            autoComplete={autocomplete}
            checked={input.value}
            required={required}
            onChange={input.onChange} onClick={input.onClick} onFocus={input.onFocus}  onBlur={input.onBlur}
            bsSize={size}
            label={ typeof label === 'string' ? `${label}${required ? <span>*</span> : ''}` : label}
            style={style}/>
        }
         
        {this.props.children}
      </div>
    )
  }

  renderRadio = () => {
    const { id, placeholder, type, name, input, label, size, noCustom, disabled, value, style, autocomplete, required} = this.props;
    return (
      <div>
        <span>{id} {name} {type}</span>
        { !noCustom &&
          <CustomInput
            type={type}
            disabled={disabled}
            id={id}
            name={id}
            placeholder={placeholder}
            autoComplete={autocomplete}
            onChange={input.onChange}  onFocus={input.onFocus}  onBlur={input.onBlur}
            bsSize={size}
            checked={(input.value === value)}
            value={input.value}
            required={required}
            label={`${label}${required ? <span>*</span> : ''}`}
            style={style}/>
        }
        { noCustom &&
          <Input
            type={type}
            name={id}
            // value={value}
            placeholder={placeholder}
            autoComplete={autocomplete}
            required={required}
            onChange={input.onChange}  onFocus={input.onFocus}  onBlur={input.onBlur}
            bsSize={size}
            checked={(input.value === value)}
            value={input.value}
            label={`${label}${required ? <span>*</span> : ''}`}
            style={style}/>
        }
         
        {this.props.children}
      </div>
    )
  }

  renderSelect = () => {
    const { id, placeholder, type, input, label, size, noCustom, disabled, style, autocomplete, required} = this.props;
    return (
      <div>
        { label &&
          <Label size={size} for={id}>{label}{required && <span>*</span>}</Label>
        }
        { !noCustom &&
          <CustomInput type={type} autoComplete={autocomplete} id={id} required={required} placeholder={placeholder} value={input.value} onChange={input.onChange}  onFocus={input.onFocus}  onBlur={input.onBlur} bsSize={size} disabled={disabled} style={style}>
            {this.props.children}
          </CustomInput>
        }
        { noCustom &&
          <Input type={type} id={id} autoComplete={autocomplete} required={required} placeholder={placeholder} value={input.value} onChange={input.onChange}  onFocus={input.onFocus}  onBlur={input.onBlur} bsSize={size} disabled={disabled} style={style}>
            {this.props.children}
          </Input>
        }
        
      </div>
    )
  }

  /** Renders the field, the label (children element) and possible errors */
  render () {
    const { type, meta: { touched, error, warning }, hint, externalValidationError} = this.props;
    let renderedInput= null;
    
    if (type === "checkbox" || type === "switch" ) {
      renderedInput = this.renderCheckbox();
    }
    else if (type === "radio") {
      renderedInput = this.renderRadio();
    }
    else if (type === "select") {
      renderedInput = this.renderSelect();
    }
    else {
      renderedInput = this.renderDefault();
    }
    
    return (
      <FormGroup>
        {renderedInput} 
        {hint && <em className="text-muted small">{hint}</em> }
        {touched && ( 
          (error && <Alert color="danger">{error}</Alert>) ||
          (warning && <span>{warning}</span>)|| 
          (externalValidationError && <Alert color="danger">{externalValidationError}</Alert>)
        )}
        {/* <Input.Feedback /> */}
      </FormGroup>
    );
  }
}
